import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { useAdminFixturesFromQuery } from '../fixtures'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { safeAdminError } from '../safe-ui'

type Kpis = {
  clientsTotal: number
  clientsVerified: number
  clientsSuspended: number
  accountsTotal: number
  accountsDemo: number
  accountsLive: number
  accountsActive: number
  pendingDeposits: number
  stuckCryptoDeposits?: number
  todayDeposits?: number
  todayCompletedDeposits?: number
  completedDeposits: number
  pendingWithdrawals: number
  completedWithdrawals: number
  ibApplicationsPending: number
  ibActive: number
  ibWalletRequestsPending: number
}

type Bridge = {
  configured: boolean
  reachable: boolean
  mt5Ready: boolean
  demoReady?: boolean
  liveReady?: boolean
  liveStatus?: string
  status: string
  message: string
}

export function AdminDashboardPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [kpis, setKpis] = useState<Kpis | null>(null)
  const [bridge, setBridge] = useState<Bridge | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (fixtures) {
          if (!cancelled) {
            setKpis({
              clientsTotal: 12,
              clientsVerified: 10,
              clientsSuspended: 1,
              accountsTotal: 18,
              accountsDemo: 12,
              accountsLive: 6,
              accountsActive: 16,
              pendingDeposits: 2,
              stuckCryptoDeposits: 0,
              todayDeposits: 1,
              todayCompletedDeposits: 1,
              completedDeposits: 40,
              pendingWithdrawals: 1,
              completedWithdrawals: 22,
              ibApplicationsPending: 1,
              ibActive: 3,
              ibWalletRequestsPending: 0,
            })
            setBridge({
              configured: true,
              reachable: true,
              mt5Ready: true,
              demoReady: true,
              liveReady: true,
              liveStatus: 'connected',
              status: 'READY',
              message: 'Fixture bridge',
            })
          }
          return
        }
        const data = await adminApi.dashboard()
        if (cancelled) return
        setKpis(data.kpis)
        setBridge(data.bridge)
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load dashboard'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fixtures])

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Master Admin" title="Business dashboard">
        <p className="cp-muted">
          Perfect Predict operations — clients, MT5, deposits/withdrawals, IB partners, bridge.
        </p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Dashboard numbers below are preview fixtures, not live operations data.
        </StateBlock>
      ) : null}

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading dashboard…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className={loading && !kpis ? 'is-refreshing' : undefined}>
        <h2>Clients &amp; accounts</h2>
        <div className="cp-kpi-grid">
          <article className="cp-kpi">
            <span>Clients</span>
            <strong>{kpis ? kpis.clientsTotal : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Email verified</span>
            <strong>{kpis ? kpis.clientsVerified : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Suspended</span>
            <strong>{kpis ? kpis.clientsSuspended : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>MT5 accounts</span>
            <strong>{kpis ? kpis.accountsTotal : '—'}</strong>
            <em>
              {kpis ? `Demo ${kpis.accountsDemo} · Live ${kpis.accountsLive}` : 'Demo — · Live —'}
            </em>
          </article>
          <article className="cp-kpi">
            <span>Active accounts</span>
            <strong>{kpis ? kpis.accountsActive : '—'}</strong>
          </article>
        </div>

        <h2 style={{ marginTop: '1.5rem' }}>Money ops</h2>
        <div className="cp-kpi-grid">
          <article className="cp-kpi">
            <span>Today&apos;s deposits</span>
            <strong>{kpis ? (kpis.todayDeposits ?? 0) : '—'}</strong>
            <em>
              {kpis
                ? `Completed today ${kpis.todayCompletedDeposits ?? 0}`
                : 'Completed today —'}
            </em>
          </article>
          <article className="cp-kpi">
            <span>Pending deposits</span>
            <strong>{kpis ? kpis.pendingDeposits : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Stuck TRC20 (paid, not credited)</span>
            <strong>{kpis ? (kpis.stuckCryptoDeposits ?? 0) : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Completed deposits</span>
            <strong>{kpis ? kpis.completedDeposits : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Pending withdrawals</span>
            <strong>{kpis ? kpis.pendingWithdrawals : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Completed withdrawals</span>
            <strong>{kpis ? kpis.completedWithdrawals : '—'}</strong>
          </article>
        </div>

        <h2 style={{ marginTop: '1.5rem' }}>IB / Sub-admins</h2>
        <div className="cp-kpi-grid">
          <article className="cp-kpi">
            <span>Pending applications</span>
            <strong>{kpis ? kpis.ibApplicationsPending : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>Active IBs</span>
            <strong>{kpis ? kpis.ibActive : '—'}</strong>
          </article>
          <article className="cp-kpi">
            <span>IB wallet requests</span>
            <strong>{kpis ? kpis.ibWalletRequestsPending : '—'}</strong>
          </article>
        </div>
      </div>

      {bridge ? (
        <div className="cp-panel" style={{ marginTop: '1.5rem' }}>
          <h2>MT5 Bridge</h2>
          <p>
            Status: <strong>{bridge.status}</strong>
            {bridge.reachable ? ' · reachable' : ' · unreachable'}
            {bridge.demoReady ? ' · Demo OK' : ' · Demo down'}
            {bridge.liveReady ? ' · Live OK' : ' · Live down'}
          </p>
          <p className="cp-muted">{bridge.message}</p>
          {(kpis?.stuckCryptoDeposits ?? 0) > 0 ? (
            <StateBlock tone="warn">
              {kpis?.stuckCryptoDeposits} TRC20 deposit(s) paid but not credited yet. Check Live
              bridge, then open Deposits and refresh.
            </StateBlock>
          ) : null}
          <Link className="pp-btn pp-btn--secondary pp-btn--sm" to={`/admin/settings${q}`}>
            Bridge settings
          </Link>
        </div>
      ) : null}

      <div className="cp-panel" style={{ marginTop: '1.5rem' }}>
        <h2>Quick actions</h2>
        <div className="cp-platform-actions">
          <Link className="pp-btn pp-btn--secondary" to={`/admin/balance${q}`}>
            MT5 Balance add/withdraw
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/deposits${q}`}>
            Deposits queue
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/withdrawals${q}`}>
            Withdrawals queue
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/ib${q}`}>
            IB approvals
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/clients${q}`}>
            Clients
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/trading-accounts${q}`}>
            Trading accounts
          </Link>
          <Link className="pp-btn pp-btn--secondary" to={`/admin/settings${q}`}>
            Settings
          </Link>
        </div>
      </div>
    </section>
  )
}
